import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import React from 'react';
import { ErrorView } from './views/ErrorView.tsx';
import { SignInView } from './views/SignInView.tsx';
import { SignUpView } from './views/SignUpView.tsx';
import { SignUpEmailView } from './views/SignUpEmailView.tsx';
import { ResetPasswordView } from './views/ResetPasswordView.tsx';
import { PrivacyPolicyView } from './views/PrivacyPolicyView.tsx';
import { UpdatePasswordView } from './views/UpdatePasswordView.tsx';
import { TermsOfServiceView } from './views/TermsOfServiceView.tsx';
import EmailConfirmation from './views/EmailConfirmation.tsx';
import { PromptView } from './views/PromptView.tsx';
import { SubscriptionView } from './views/SubscriptionView.tsx';
import { AuthGuard } from './components/auth/AuthGuard.tsx';
import { Layout } from './components/Layout.tsx';
import SettingsView from './views/SettingsView.tsx';
import { isSupabaseConfigMissing } from './lib/supabase.ts';
import { lazyWithRetry } from './lib/lazyWithRetry.tsx';
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary.tsx';
import { MissingConfig } from './components/MissingConfig';
import { ViewerRouteFallback } from './components/ViewerRouteFallback.tsx';
import { syncAnonZeroFromUrl } from './lib/devAnonZero.ts';

// ── Viewer routes — lazy boundary so three.js can leave the landing load (#261)
// EditorView/ShareView/FilesView transitively pull the three.js / R3F / drei
// viewer stack (now its own `three` chunk; see vite.config.ts manualChunks).
// The single lazy identity for each lives in views/lazyViews.tsx — shared with
// the tab shell (LayoutContent/EditorTabPane), which previously statically
// imported the same modules and pulled `three` back onto the landing critical
// path (the A2 leak, closed 2026-07-01). Desktop idle-prefetches the chunk
// below so entering the editor has no perceptible delay.
//
// SAFETY (the #57/#59 lazy-viewer revert, commit 8e01bcd): that earlier attempt
// was rolled back because a bare drei <Environment> SUSPENDED while its HDR
// decoded and, with no <Suspense> INSIDE the Canvas, R3F re-threw the suspension
// OUT to the route-level boundary — unmounting the viewer subtree, disposing the
// WebGL renderer, and flashing the "grey screen of death". That ROOT CAUSE was
// fixed at the source in #130 (commit fbe61bd, in `main`): ThreeScene now
// preloads the HDR at module scope and wraps <Environment> in a LOCAL
// <Suspense>, so the decode can never reach a route boundary. The revert reason
// is resolved; re-introducing the lazy boundary is safe.
import { EditorView, FilesView, ShareView } from './views/lazyViews.tsx';

// Scan-to-CAD is a light (no three.js) flag-gated view; lazy-load it directly so
// it costs nothing until the `/scan-to-cad` route is hit.
const ScanToCadView = lazyWithRetry(() => import('./views/ScanToCadView.tsx'));

// Mesh-to-STEP — the ANALYTIC-tier mesh → STEP view; same light, flag-gated
// shape as Scan-to-CAD, lazy-loaded so it costs nothing until `/mesh-to-step`.
const MeshToStepView = lazyWithRetry(
  () => import('./views/MeshToStepView.tsx'),
);

// DEV-only material harnesses also statically pulled three/ThreeScene into the
// entry chunk; make them lazy too (matching the sibling DEV harnesses below) so
// nothing in the always-loaded graph references the viewer stack.
const MaterialHarness = lazyWithRetry(
  () => import('./views/MaterialHarness.tsx'),
);
const MaterialDockHarness = lazyWithRetry(
  () => import('./views/MaterialDockHarness.tsx'),
);
// Lazy — pulls in the heavy <ThreeScene>; keep it out of the entry chunk so the
// DEV-only #130 perf-baseline harness never affects prod first-paint.
// `lazyWithRetry` (vs bare React.lazy) retries a transient chunk fetch once and,
// on a hard failure, surfaces a tagged ChunkLoadError that <ChunkErrorBoundary>
// turns into a "new version — Reload" affordance instead of the bare router Oops.
const PerfHarness = lazyWithRetry(() => import('./views/PerfHarness.tsx'));
const PathTraceTest = lazyWithRetry(() => import('./views/PathTraceTest.tsx'));
const PtDemo = lazyWithRetry(() => import('./views/PtDemo.tsx'));
const RTXTest = lazyWithRetry(() => import('./views/RTXTest.tsx'));
const ImageToCadFoldHarness = lazyWithRetry(
  () => import('./views/ImageToCadFoldHarness.tsx'),
);
// Lazy + DEV-gated: point-cloud generation-visualization harness (GlbPreview).
const PointCloudHarness = lazyWithRetry(
  () => import('./views/PointCloudHarness.tsx'),
);
// Lazy + DEV-gated: the #58 thumbnail-bake harness pulls in <ThreePreview>'s
// three/R3F stack; keep it out of the entry chunk so it never affects prod.
const AcImportHarness = lazyWithRetry(
  () => import('./views/AcImportHarness.tsx'),
);
const JawsWorkbenchHarness = lazyWithRetry(
  () => import('./views/JawsWorkbenchHarness.tsx'),
);
const JawsMotionHarness = lazyWithRetry(
  () => import('./views/JawsMotionHarness.tsx'),
);
const ThumbnailBakeHarness = lazyWithRetry(
  () => import('./views/ThumbnailBakeHarness.tsx'),
);
const Model3ScanHarness = lazyWithRetry(
  () => import('./views/Model3ScanHarness.tsx'),
);
// Lazy + DEV-gated: the scan→CAD workbench P0.5 fixture viewer (build plan).
const ScanToCadWorkbenchHarness = lazyWithRetry(
  () => import('./views/ScanToCadWorkbenchHarness.tsx'),
);
// Lazy + DEV-gated: Mesh2BREP upload -> edge fn -> STEP solid harness.
const Mesh2BrepHarness = lazyWithRetry(
  () => import('./views/Mesh2BrepHarness.tsx'),
);
// Lazy + DEV-gated: spaceframe chassis viewport on a fixture store.
const SpaceframeHarness = lazyWithRetry(
  () => import('./views/SpaceframeHarness.tsx'),
);
// Lazy + DEV-gated: the #167 interference-CSG-worker perf harness mounts the
// heavy <ThreeScene>; keep it out of the entry chunk so it never affects prod.
const InterferencePerfHarness = lazyWithRetry(
  () => import('./views/InterferencePerfHarness.tsx'),
);
// Lazy + DEV-gated: the anon-out-of-tokens harness launcher. The simulation
// itself is the flag-driven override in AuthProvider (lib/devAnonZero); this
// route is just the entry page.
const AnonZeroHarness = lazyWithRetry(
  () => import('./views/AnonZeroHarness.tsx'),
);

/** Wrap a lazy route element in the viewer-route Suspense fallback. */
function suspended(element: React.ReactNode) {
  return (
    <React.Suspense fallback={<ViewerRouteFallback />}>
      {element}
    </React.Suspense>
  );
}

// Idle-prefetch the viewer chunk once the app is interactive so a desktop user
// who navigates into the editor sees NO download delay (the #261 constraint:
// the RTX/desktop experience must not regress). This only WARMS the module
// cache off the critical path — it does not mount the viewer, render, or touch
// WebGL — so it has zero effect on first paint or the render loop. `requestIdle`
// keeps it off the main-thread budget; the dynamic import resolves to the same
// `three` chunk the route would otherwise fetch on click.
function prefetchViewerChunk() {
  const warm = () => {
    void import('./views/EditorView.tsx');
  };
  const ric = (
    window as typeof window & {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number },
      ) => void;
    }
  ).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(warm, { timeout: 3000 });
  } else {
    // Safari/older: fall back to a post-load timeout so we still warm off the
    // critical path without blocking first paint.
    window.setTimeout(warm, 1500);
  }
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN ?? '',
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'local',
  tracesSampleRate: 1.0,
});

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      errorElement: <ErrorView />,
      children: [
        { path: '/signin', element: <SignInView /> },
        // Dev-only UX test harnesses — kept out of production. `import.meta.env.DEV`
        // is statically `false` in prod builds, so this branch (and the harness
        // imports it references) is dead-code-eliminated from the bundle. They
        // stay fully usable in `npm run dev`. See agents.md "Dev/test harnesses".
        ...(import.meta.env.DEV
          ? [
              {
                path: '/material-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <MaterialHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/material-dock-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <MaterialDockHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/perf-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <PerfHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/pt-test',
                element: (
                  <React.Suspense fallback={null}>
                    <PathTraceTest />
                  </React.Suspense>
                ),
              },
              {
                path: '/image-to-cad-fold-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <ImageToCadFoldHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/pointcloud-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <PointCloudHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/thumbnail-bake-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <ThumbnailBakeHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/model3-scan-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <Model3ScanHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/scan-to-cad-workbench-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <ScanToCadWorkbenchHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/mesh2brep-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <Mesh2BrepHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/spaceframe-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <SpaceframeHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/ac-import-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <AcImportHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/jaws-workbench',
                element: (
                  <React.Suspense fallback={null}>
                    <JawsWorkbenchHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/jaws-motion-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <JawsMotionHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/interference-perf-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <InterferencePerfHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/anon-zero-harness',
                element: (
                  <React.Suspense fallback={null}>
                    <AnonZeroHarness />
                  </React.Suspense>
                ),
              },
              {
                path: '/pt-demo',
                element: (
                  <React.Suspense fallback={null}>
                    <PtDemo />
                  </React.Suspense>
                ),
              },
              {
                path: '/rtx-test',
                element: (
                  <React.Suspense fallback={null}>
                    <RTXTest />
                  </React.Suspense>
                ),
              },
            ]
          : []),
        { path: '/signup', element: <SignUpView /> },
        { path: '/signup-email', element: <SignUpEmailView /> },
        { path: '/reset-password', element: <ResetPasswordView /> },
        { path: '/confirm-email', element: <EmailConfirmation /> },
        { path: '/privacy-policy', element: <PrivacyPolicyView /> },
        { path: '/terms-of-service', element: <TermsOfServiceView /> },
        { path: '/update-password', element: <UpdatePasswordView /> },
        {
          element: <Layout />,
          children: [
            {
              path: '/',
              element: <PromptView />,
              errorElement: <ErrorView />,
            },
            {
              path: '/share/:id',
              element: suspended(<ShareView />),
              errorElement: <ErrorView />,
            },
            {
              element: (
                <AuthGuard>
                  <Outlet />
                </AuthGuard>
              ),
              children: [
                {
                  path: '/editor/:id',
                  element: suspended(<EditorView />),
                  errorElement: <ErrorView />,
                },
                {
                  path: '/files',
                  errorElement: <ErrorView />,
                  element: suspended(<FilesView />),
                },
                {
                  path: '/market',
                  errorElement: <ErrorView />,
                  element: suspended(<FilesView />),
                },
                {
                  // Category shelves live at /market/<slug>. Same element as
                  // /market — LayoutContent swaps in MarketplaceView for any
                  // /market path, exactly as it does for /files/*.
                  path: '/market/*',
                  errorElement: <ErrorView />,
                  element: suspended(<FilesView />),
                },
                {
                  path: '/files/*',
                  errorElement: <ErrorView />,
                  element: suspended(<FilesView />),
                },
                {
                  path: '/scan-to-cad',
                  errorElement: <ErrorView />,
                  element: suspended(<ScanToCadView />),
                },
                {
                  path: '/mesh-to-step',
                  errorElement: <ErrorView />,
                  element: suspended(<MeshToStepView />),
                },
                {
                  path: '/subscription',
                  errorElement: <ErrorView />,
                  element: <SubscriptionView />,
                },
                {
                  path: '/settings',
                  errorElement: <ErrorView />,
                  element: <SettingsView />,
                },
              ],
            },
            { path: '*', element: <Navigate to="/" replace /> },
          ],
        },
      ],
    },
  ],
  { future: { v7_relativeSplatPath: true } },
);

// Apply the dev-only anon-out-of-tokens flag from the URL (?anonZero=1&tokens=N)
// to localStorage before the app boots, so AuthProvider resolves the session
// under the override on first render. No-op outside DEV.
syncAnonZeroFromUrl();

createRoot(document.getElementById('root')!).render(
  isSupabaseConfigMissing ? (
    <MissingConfig />
  ) : (
    <StrictMode>
      {/* ChunkErrorBoundary above the router so a failed dynamic import()
          (chunk 404 after a redeploy, dropped mobile connection) shows a Reload
          affordance instead of the bare router Oops. Real bugs are re-thrown to
          the router errorElement. */}
      <ChunkErrorBoundary>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </ChunkErrorBoundary>
    </StrictMode>
  ),
);

// App is now mounting — warm the viewer chunk on idle so entering the editor on
// desktop has no download delay (#261: no desktop/RTX regression). No-op for
// first paint and the render loop; only prefetches when the real app booted.
if (!isSupabaseConfigMissing) {
  prefetchViewerChunk();
}
